/**
 * Guessing what the other nine teams will keep.
 *
 * The mock draft has always had a hole in it: it placed only the keepers that had actually been
 * declared, so before the deadline it drafted a board with forty of the best players still in the
 * pool. Every mock run in August was therefore a mock of a draft that will not happen. This is
 * the missing half — you say what you think they will keep, and the sandbox board reflects it.
 *
 * PRIVATE, AND SAVED. Private because a full set of guesses is a valuation of everybody, which is
 * worth more than any single slate; `firestore.rules` refuses every other manager, commissioners
 * included. Saved because ten teams is a lot of typing to lose to a refresh on draft night.
 *
 * WHAT IS COPIED IN AND WHAT IS TYPED. Submitted slates are facts and are seeded automatically,
 * marked as such, and locked — there is nothing to guess about a man who has told the league what
 * he is keeping. A DRAFT slate is never read: the database would allow it, since declarations are
 * league-readable so the docket works, but using it would turn somebody's working note into a
 * leak. Everything else is yours to fill in.
 */

import { useEffect, useMemo, useState } from 'react';
import { LEAGUE_2026 } from '../domain/config.js';
import { conflicts, resync, seedFrom } from '../domain/mockKeepers.js';
import { managerShort } from '../domain/identity.js';
import { NflMark } from './NflChip.js';
import { Rule } from './RuleText.js';
import type {
  KeeperDeclaration, LeagueStore, MockKeeper, MockKeeperSet,
} from '../store/types.js';

const SEASON = 2026;

export interface KeeperOption {
  player: string;
  gm: string;
  pos?: string;
  cost: number | null;
  mandatory?: boolean;
  farm?: boolean;
}

export function MockKeepers(
  { gm, order, options, store, onGoBoard }: {
    gm: string;
    /** Every GM, in draft order, so the list reads the way the board does. */
    order: readonly string[];
    /** Everybody's keeper candidates, as the keeper build computes them. */
    options: readonly KeeperOption[];
    store: LeagueStore;
    onGoBoard?: () => void;
  },
) {
  const [set, setSet] = useState<MockKeeperSet | null>(null);
  const [declarations, setDeclarations] = useState<KeeperDeclaration[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);
  /** The promote-to-draft action: in flight, and whether it has landed since the last change. */
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    let live = true;
    void Promise.all([store.mockKeepers(SEASON, gm), store.declarations(SEASON)])
      .then(([m, d]) => {
        if (!live) return;
        setSet(m);
        setDeclarations(d);
        setLoaded(true);
      });
    return () => { live = false; };
  }, [store, gm]);

  const clash = useMemo(
    () => (set ? conflicts(set, declarations) : []),
    [set, declarations],
  );

  /** Candidates grouped by the team that holds them. */
  const byGm = useMemo(() => {
    const m = new Map<string, KeeperOption[]>();
    for (const o of options) {
      if (o.cost === null) continue;
      const who = o.gm.toUpperCase();
      if (!m.has(who)) m.set(who, []);
      m.get(who)!.push(o);
    }
    for (const list of m.values()) list.sort((a, b) => (a.cost ?? 99) - (b.cost ?? 99));
    return m;
  }, [options]);

  const declaredGms = useMemo(
    () => new Set(declarations.filter((d) => d.status === 'submitted').map((d) => d.gm.toUpperCase())),
    [declarations],
  );

  async function save(next: MockKeeperSet) {
    setBusy(true);
    setSet(next);
    try {
      await store.saveMockKeepers(next);
    } finally {
      setBusy(false);
    }
  }

  const current = (): MockKeeperSet => set
    ?? seedFrom(SEASON, gm, declarations, new Date().toISOString());

  /** Which team the manager last tried to over-fill, so the refusal appears on that card. */
  const [tooMany, setTooMany] = useState<string | null>(null);
  /** 17f: which team's slate the editor is open on, and the field over his pool. */
  const [editing, setEditing] = useState<string | null>(null);
  const [mkq, setMkq] = useState('');

  function toggle(who: string, player: string) {
    if (isForced(who, player)) return;   // 2013-III-1 §5 — not a choice anybody has
    const base = current();
    const rows = base.byGm[who] ?? [];
    const has = rows.some((r) => r.player === player);
    /*
     * EIGHT IS A LIMIT, not a suggestion, so a guess cannot exceed it. A nine-man guessed slate
     * is a guess at something that cannot happen, and the board built from it would hold a
     * player back who is really in the pool — the same reasoning that merges the mandatory
     * graduates in on read.
     */
    if (!has && picked(who).length >= LEAGUE_2026.maxKeepers) { setTooMany(who); return; }
    setTooMany(null);
    setSaved(false);
    const next: MockKeeper[] = has
      ? rows.filter((r) => r.player !== player)
      : [...rows, { player }];
    void save({
      ...base,
      byGm: { ...base.byGm, [who]: next },
      updatedAt: new Date().toISOString(),
    });
  }

  /**
   * What is showing for a team: the saved rows, plus every mandatory farm graduate whether he was
   * saved or not.
   *
   * 2013-III-1 §5 makes a graduate's rookie year mandatory, so a guessed slate that omitted him
   * would be a guess at something that cannot happen — and the mock board built from it would
   * hand the manager a receiver who is not actually in the pool. Merged on read rather than
   * written in, so the set stays a record of what he actually chose.
   */
  const picked = (who: string) => {
    /*
     * MY OWN TEAM IS MINE TO PLAY WITH IN HERE, seeded from what the league already has.
     *
     * It used to be pinned to my real declaration on the grounds that it is the one team I cannot
     * be wrong about — which also made the mock the one place I could not try a slate. The seed
     * is my SUBMITTED keepers (`seedFrom` reads nothing else), so I start from the real thing;
     * what I do from there stays here until I promote it.
     */
    if (who === gm.toUpperCase() && !set?.byGm[who]) {
      const mine = declarations.find((d) => d.gm.toUpperCase() === who && d.status === 'submitted');
      return mine ? mine.keepers.map((k) => k.player) : [];
    }
    const saved = (set?.byGm[who] ?? []).map((r) => r.player);
    const forced = (byGm.get(who) ?? []).filter((o) => o.mandatory).map((o) => o.player);
    return [...new Set([...forced, ...saved])];
  };

  const myStatus = declarations.find(
    (d) => d.gm.toUpperCase() === gm.toUpperCase(),
  )?.status ?? null;

  /**
   * Promote what I have built in here to a real DRAFT declaration.
   *
   * A draft rather than a submission, deliberately: submitting is a statement to nine other
   * managers and it belongs on the screen that says so, with the tax and the roster in front of
   * you. This is the sandbox handing its work over, not speaking for you.
   *
   * `store.saveDeclaration` reaches the real store for my own team only — see `sandboxFrom`.
   */
  async function saveMine() {
    const me = gm.toUpperCase();
    const mine = picked(me);
    setSaving(true);
    try {
      const was = declarations.find((d) => d.gm.toUpperCase() === me);
      await store.saveDeclaration({
        season: SEASON,
        gm: me,
        status: 'draft',
        updatedAt: new Date().toISOString(),
        keepers: mine.map((player) => {
          const before = was?.keepers.find((k) => k.player === player);
          return {
            player,
            /* The tag and the round pin are the manager's own earlier decisions about these
               men; carrying them over stops a promote from silently un-tagging somebody. */
            ...(before?.franchiseTagged ? { franchiseTagged: true } : {}),
            ...(before?.elevatedTo ? { elevatedTo: before.elevatedTo } : {}),
          };
        }),
      });
      setDeclarations(await store.declarations(SEASON));
      setSaved(true);
    } finally {
      setSaving(false);
    }
  }

  const isForced = (who: string, player: string) =>
    (byGm.get(who) ?? []).some((o) => o.player === player && o.mandatory);

  if (!loaded) return <p className="note railpad">Reading your guesses…</p>;

  return (
    <div className="mockkeepers">
      <section className="card">

        {clash.length > 0 && (
          <div className="mkclash">
            <b>
              {clash.length} team{clash.length === 1 ? '' : 's'} moved since you last synced
            </b>
            <ul>
              {clash.map((c) => (
                <li key={c.gm}>
                  <b>{managerShort(c.gm)}</b>
                  <span>{c.says}</span>
                  <span className="mut">
                    {c.kind === 'now-declared'
                      ? `you had ${c.guessed.length || 'nobody'}, he kept ${c.actual.length}`
                      : `${c.actual.length} on his slate now`}
                  </span>
                </li>
              ))}
            </ul>
            {/*
              CLEAR AND RE-SYNC clears only the rows facts have overtaken. Guesses about teams
              that have not declared survive — pressing the button that tells you that you were
              out of date should not cost you the rest of your work.
            */}
            <button type="button" className="btn small" disabled={busy}
              onClick={() => void save(resync(current(), declarations, new Date().toISOString()))}>
              Clear those and re-sync
            </button>
          </div>
        )}

        {/*
          17f: ONE ROW PER TEAM, ONE EDITOR. The rows say where each slate stands in a sentence
          and a pill; tapping a row opens the editor under the list, with a field over his
          keepable pool and the guesses priced. Submitted slates are seeded and locked.
        */}
        <p className="mkhead2 mut">
          Saved privately, refused to every other manager, commissioners included. The mock places
          these instead of the real declarations.
        </p>
        <ul className="mkrows">
          {order.map((who) => {
            const w = who.toUpperCase();
            const isMe = w === gm.toUpperCase();
            const locked = declaredGms.has(w);
            const chosen = picked(w);
            const pool = byGm.get(w) ?? [];
            const named = chosen.slice(0, 2).map((pl) => {
              const o = pool.find((x) => x.player === pl);
              const sur = pl.split(' ').slice(-1)[0];
              return o?.cost != null ? `${sur} Rd ${o.cost}` : sur;
            }).join(' · ');
            const say = locked
              ? (isMe ? `your slate${named ? ` — ${named}` : ''}`
                : `slate submitted — ${chosen.length} kept${named ? `, ${named}` : ''}`)
              : chosen.length === 0 ? 'no slate submitted — nothing guessed yet'
                : `no slate submitted — ${chosen.length} of ${LEAGUE_2026.maxKeepers} guessed`;
            const open = editing === w;
            return (
              <li key={w} className={`mkrow${locked ? ' declared' : ''}${isMe ? ' me' : ''}${open ? ' open' : ''}`}>
                <button type="button" className="mkrowbtn" aria-expanded={open}
                  onClick={() => { setEditing(open ? null : w); setMkq(''); }}>
                  <b>{managerShort(w)}</b>
                  <span className="mksay">{say}</span>
                  <span className={`mkpill ${locked ? 'locked' : 'guess'}`}>
                    {locked ? 'Seeded · locked' : 'Your guess'}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>

        {editing && (() => {
          const w = editing;
          const isMe = w === gm.toUpperCase();
          const locked = declaredGms.has(w);
          const chosen = picked(w);
          const pool = byGm.get(w) ?? [];
          const q = mkq.trim().toLowerCase();
          const matches = q
            ? pool.filter((o) => !chosen.includes(o.player) && o.player.toLowerCase().includes(q)).slice(0, 8)
            : [];
          const fixedAll = locked && !isMe;
          return (
            <section className="card mkeditor">
              <div className="mkedhead">
                <h3 className="striphead">
                  {isMe ? 'Your slate' : `Guessing ${managerShort(w)}'s slate`}
                </h3>
                <span className={`mkpill ${locked ? 'locked' : 'guess'}`}>
                  {locked ? 'Seeded · locked' : `Your guess · ${chosen.length} of ${LEAGUE_2026.maxKeepers}`}
                </span>
              </div>
              {!fixedAll && (
                <label className="mksearch">
                  <span aria-hidden="true">⌕</span>
                  <input type="search" value={mkq} placeholder={isMe ? 'Add a player you might keep…' : 'Add a player he might keep…'}
                    aria-label="Add a keeper guess" onChange={(e) => setMkq(e.target.value)} />
                </label>
              )}
              {matches.length > 0 && (
                <ul className="mkmatches">
                  {matches.map((o) => (
                    <li key={o.player}>
                      <button type="button" disabled={busy}
                        onClick={() => { toggle(w, o.player); setMkq(''); }}>
                        <span className="mkname">{o.player}</span>
                        <NflMark player={o.player} />
                        {o.pos && <span className={`poschip ident pos-${o.pos}`}>{o.pos}</span>}
                        <span className="mkcost">costs Rd {o.cost}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              {q && matches.length === 0 && (
                <p className="mknote">Nobody keepable on his roster matches that.</p>
              )}
              <ul className="mkchosen">
                {chosen.length === 0 && <li className="mut mkempty">Nothing guessed yet.</li>}
                {chosen.map((pl) => {
                  const o = pool.find((x) => x.player === pl);
                  const [first, ...rest] = pl.split(' ');
                  const last = rest.length ? rest.join(' ') : first;
                  const fixed = fixedAll || o?.mandatory === true;
                  return (
                    <li key={pl}>
                      <span className="mkwho"><b>{last}</b>{rest.length ? ` ${first}` : ''}</span>
                      <span className="mkcost">{o?.cost != null ? `costs Rd ${o.cost}` : ''}</span>
                      {fixed
                        ? <span className="mkfixed" title={o?.mandatory
                            ? 'Farm graduate, his rookie year is mandatory (2013-III-1 §5)'
                            : 'Submitted, not a guess'}>{o?.mandatory ? '◆' : '§'}</span>
                        : <button type="button" className="mkx" aria-label={`Take ${pl} off`}
                            disabled={busy} onClick={() => toggle(w, pl)}>×</button>}
                    </li>
                  );
                })}
              </ul>
              {tooMany === w && (
                <p className="mknote bad">Eight is the most anybody can keep. Take one off first.</p>
              )}
              <p className="mknote">
                {fixedAll
                  ? <>Submitted — his actual slate, not a guess. <Rule id="2020-II-1">§</Rule></>
                  : `The Grid prices each guess; the slate caps at ${LEAGUE_2026.maxKeepers}. Guesses land on the board as this mock's keepers.`}
              </p>
              {isMe && (
                <div className="mkmine">
                  <p className="mknote">
                    Yours, seeded from your submitted slate. Nothing here reaches the league
                    until you save it as a draft.
                  </p>
                  <button type="button" className="btn small" disabled={busy || saving}
                    title="Write these to your real declaration as a DRAFT — you still submit it on Keepers › Declare"
                    onClick={() => void saveMine()}>
                    {saved ? 'Saved as a draft' : 'Save as my keeper draft'}
                  </button>
                  {myStatus === 'submitted' && (
                    <p className="mknote bad">
                      Your slate is submitted. Saving a draft takes it back OUT of the league's
                      submitted list until you submit again.
                    </p>
                  )}
                </div>
              )}
            </section>
          );
        })()}

        <div className="actions">
          {onGoBoard && (
            <button type="button" className="btn primary" onClick={onGoBoard}>
              Mock the draft with these
            </button>
          )}
          <span className="mut">
            {set?.updatedAt ? `saved ${set.updatedAt.slice(0, 10)}` : 'nothing saved yet'}
          </span>
        </div>
      </section>
    </div>
  );
}
